import { Stack, Typography, useTheme } from '@mui/material'
import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
  startOfDay,
  endOfDay,
  subYears,
} from 'date-fns'
import { useMemo } from 'react'
import { ChartDataProvider } from '@mui/x-charts/ChartDataProvider'
import { ChartsSurface } from '@mui/x-charts/ChartsSurface'
import {
  AreaPlot,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from '@mui/x-charts/LineChart'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis'
import { useGetVisitors } from '~/features/hooks/useGetVisitors'
import { IconCoinFilled } from '@tabler/icons-react'
import * as R from 'remeda'
import { useRole } from '~/features/auth/useRole'
import { getAgencyScopeOrFilter } from '../auth/utils'

import { OverviewCard } from '~/ui/components/OverviewCard'
import { ChartsAxisHighlight, ChartsGrid } from '@mui/x-charts'
import { CustomTooltip } from '~/features/dashboard/components/CustomTooltip'
import { getChartColors, getCommonYaxisProps, LAST_YEAR_SERIES, THIS_YEAR_SERIES } from './utils'

const now = new Date()
const startOfThisYear = startOfYear(now)
const startOfLastYear = startOfYear(subYears(now, 1))
const endOfLastYear = endOfYear(startOfLastYear)
const startOfToday = startOfDay(now)
const endOfToday = endOfDay(now)
const startOfThisMonth = startOfMonth(now)
const endOfThisMonth = endOfMonth(now)

const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => format(new Date(2000, i, 1), 'MMM'))

const allLastYearMonths = Array.from({ length: 12 }, (_, i) => ({
  startDate: startOfMonth(new Date(startOfLastYear.getFullYear(), i, 1)),
  endDate: endOfMonth(new Date(startOfLastYear.getFullYear(), i, 1)),
}))

const allThisYearMonths = Array.from({ length: 12 }, (_, i) => ({
  startDate: startOfMonth(new Date(now.getFullYear(), i, 1)),
  endDate: endOfMonth(new Date(now.getFullYear(), i, 1)),
}))

export const Dashboard = () => {
  const theme = useTheme()
  const { role } = useRole()
  const { data: visitors, isLoading: isLoadingVisitor } = useGetVisitors({
    filters: [
      { column: 'created_at', operator: 'gt', value: startOfLastYear.toISOString() },
      { column: 'has_refund', operator: 'eq', value: false },
    ],
    orFilter: getAgencyScopeOrFilter(role),
  })

  const {
    lastYearMonthlySales,
    thisYearMonthlySales,
    todaySales,
    thisMonthSales,
    thisYearSales,
    lastYearSales,
    yMax,
    monthlyBreakdown,
  } = useMemo(() => {
    const visitorsInRange = (start: Date, end: Date) => {
      if (!visitors) return []
      return R.pipe(
        visitors,
        R.filter((v) => {
          const d = new Date(v.created_at)
          return d >= start && d <= end
        })
      )
    }

    const salesForRange = (start: Date, end: Date) =>
      R.sumBy(visitorsInRange(start, end), (v) => v.total)

    const breakdownForRange = (start: Date, end: Date) => {
      const rangeVisitors = visitorsInRange(start, end)
      const toRanked = (key: 'agency' | 'country') =>
        R.sortBy(
          R.entries(R.groupBy(rangeVisitors, (v) => v[key])).map(([label, vs]) => ({
            label,
            count: vs.length,
            total: R.sumBy(vs, (v) => v.total),
          })),
          [(item) => item.total, 'desc']
        )
      return {
        count: rangeVisitors.length,
        byAgency: toRanked('agency'),
        byCountry: toRanked('country'),
      }
    }

    const lastYearMonthlySales = R.map(allLastYearMonths, ({ startDate, endDate }) =>
      salesForRange(startDate, endDate)
    )
    const thisYearMonthlySales = R.map(allThisYearMonths, ({ startDate, endDate }) =>
      startDate <= now ? salesForRange(startDate, endDate) : null
    )

    return {
      lastYearMonthlySales,
      thisYearMonthlySales,
      todaySales: salesForRange(startOfToday, endOfToday),
      thisMonthSales: salesForRange(startOfThisMonth, endOfThisMonth),
      thisYearSales: salesForRange(startOfThisYear, endOfToday),
      lastYearSales: salesForRange(startOfLastYear, endOfLastYear),
      yMax: Math.max(
        ...lastYearMonthlySales,
        ...thisYearMonthlySales.flatMap((v) => (v === null ? [] : [v]))
      ),
      monthlyBreakdown: {
        lastYear: R.map(allLastYearMonths, ({ startDate, endDate }) =>
          breakdownForRange(startDate, endDate)
        ),
        thisYear: R.map(allThisYearMonths, ({ startDate, endDate }) =>
          breakdownForRange(startDate, endDate)
        ),
      },
    }
  }, [visitors])

  const { primaryColor, secondaryColor, textColor } = getChartColors(theme)

  return (
    <Stack px={{ xs: 2, sm: 4 }} sx={{ gap: 2, flex: 1 }}>
      <Stack sx={{ py: 2, flex: 1 }} gap={2}>
        {isLoadingVisitor ? (
          <Stack sx={{ height: 400, alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading chart data...</Typography>
          </Stack>
        ) : (
          <Stack gap={4} flex={1}>
            <Typography variant="h6">Monthly Sales</Typography>
            <Stack gap={2} flex={1}>
              <Stack direction="row" gap={2} flexWrap="wrap">
                <OverviewCard
                  Icon={IconCoinFilled}
                  value={<Typography>{todaySales.toLocaleString()} CZK</Typography>}
                  description="Today's Sales"
                  color="primary"
                />
                <OverviewCard
                  Icon={IconCoinFilled}
                  value={<Typography>{thisMonthSales.toLocaleString()} CZK</Typography>}
                  description="This Month's Sales"
                  color="secondary"
                />
                <OverviewCard
                  Icon={IconCoinFilled}
                  value={<Typography>{thisYearSales.toLocaleString()} CZK</Typography>}
                  description="This Year's Sales"
                  color="tertiary"
                />
                <OverviewCard
                  Icon={IconCoinFilled}
                  value={<Typography>{lastYearSales.toLocaleString()} CZK</Typography>}
                  description="Last Year's Sales"
                  color="primary"
                />
              </Stack>
              <ChartDataProvider
                series={[
                  {
                    type: 'line',
                    data: lastYearMonthlySales,
                    label: `${startOfLastYear.getFullYear()}`,
                    color: secondaryColor,
                    curve: 'linear',
                    id: LAST_YEAR_SERIES,
                    yAxisId: LAST_YEAR_SERIES,
                  },
                  {
                    id: THIS_YEAR_SERIES,
                    yAxisId: THIS_YEAR_SERIES,
                    type: 'line',
                    data: thisYearMonthlySales,
                    label: `${now.getFullYear()}`,
                    color: primaryColor,
                    connectNulls: false,
                    curve: 'linear',
                    area: true,
                  },
                ]}
                xAxis={[
                  {
                    data: MONTH_LABELS,
                    scaleType: 'band',
                    disableLine: true,
                    disableTicks: true,
                    categoryGapRatio: 0,
                    tickLabelStyle: { fill: theme.palette.text.secondary },
                  },
                ]}
                yAxis={[
                  {
                    id: THIS_YEAR_SERIES,
                    ...getCommonYaxisProps({ color: primaryColor, textColor, max: yMax }),
                  },
                  {
                    id: LAST_YEAR_SERIES,
                    ...getCommonYaxisProps({ color: secondaryColor, textColor, max: yMax }),
                  },
                ]}
                height={300}
              >
                <ChartsSurface
                  sx={{
                    circle: {
                      r: 3,
                      strokeWidth: 1,
                      fill: theme.palette.background.default,
                    },
                    [`& .${markElementClasses.series}-${LAST_YEAR_SERIES}`]: {
                      stroke: secondaryColor,
                    },
                    [`& .${markElementClasses.series}-${THIS_YEAR_SERIES}`]: {
                      stroke: primaryColor,
                    },
                    // The library auto-generates class names following the pattern Mui<Component>-series-<seriesId>
                    [`& .${lineElementClasses.series}-${LAST_YEAR_SERIES}`]: {
                      stroke: secondaryColor,
                      strokeDasharray: 14,
                    },
                    [`& .${lineElementClasses.series}-${THIS_YEAR_SERIES}`]: {
                      stroke: primaryColor,
                    },
                  }}
                >
                  <CustomTooltip
                    thisYear={monthlyBreakdown.thisYear}
                    lastYear={monthlyBreakdown.lastYear}
                  />
                  <ChartsGrid horizontal />
                  <LinePlot />
                  <MarkPlot />
                  <AreaPlot />
                  <ChartsAxisHighlight x="band" />
                  <ChartsXAxis />
                  <ChartsYAxis />
                </ChartsSurface>
              </ChartDataProvider>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
